import { useMemo, useState } from "react";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Input, Select, useToast } from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { Panel } from "@/modules/hub-kit/HubKit";
import { downloadTextFile, formatCount } from "@/modules/hub-kit/format";
import { createFinanceOverviewApi, type ExportKind } from "../overviewApi";

const KINDS: { value: ExportKind; label: string; description: string }[] = [
  {
    value: "transactions",
    label: "Transactions",
    description: "Every money record: payments, refunds, adjustments, with Xendit references.",
  },
  {
    value: "refunds",
    label: "Refunds",
    description: "Requested, approved and issued refunds, by request date.",
  },
  {
    value: "payouts",
    label: "Payouts",
    description: "Payouts to businesses and creators, by the date they became eligible.",
  },
  {
    value: "settlements",
    label: "Settlements",
    description: "Expected against settled amounts, for reconciling with the bank statement.",
  },
];

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function FinanceExportsTab() {
  const client = useApiClient();
  const api = useMemo(() => createFinanceOverviewApi(client), [client]);
  const toast = useToast();
  const today = new Date();
  const [kind, setKind] = useState<ExportKind>("transactions");
  const [from, setFrom] = useState(isoDay(new Date(today.getTime() - 30 * 86400000)));
  const [to, setTo] = useState(isoDay(today));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [last, setLast] = useState<{ rows: number; truncated: boolean } | null>(null);

  const invalid = !from || !to || from > to;

  async function run() {
    setBusy(true);
    setError(null);
    setLast(null);
    try {
      // Day boundaries in Manila time (UTC+8), the business day.
      const fromIso = new Date(`${from}T00:00:00+08:00`).toISOString();
      const toIso = new Date(new Date(`${to}T00:00:00+08:00`).getTime() + 86400000).toISOString();
      const result = await api.exportCsv(kind, fromIso, toIso);
      if (result.row_count === 0) {
        setLast({ rows: 0, truncated: false });
        toast.push({ title: "Nothing to export for that period", tone: "info" });
        return;
      }
      downloadTextFile(`huddl-${kind}-${from}_to_${to}.csv`, result.csv);
      setLast({ rows: result.row_count, truncated: result.truncated });
      toast.push({ title: `Exported ${formatCount(result.row_count)} rows`, tone: "success" });
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const current = KINDS.find((k) => k.value === kind)!;

  return (
    <Stack gap={16}>
      <Panel
        title="Export to CSV"
        description="For your accountant, the bank reconciliation and audits. Amounts are decimal pesos exactly as the ledger holds them."
      >
        <Flex gap={12} align="end" wrap>
          <Select
            label="What to export"
            name="export-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as ExportKind)}
            options={KINDS.map((k) => ({ value: k.value, label: k.label }))}
          />
          <Input
            label="From"
            name="export-from"
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
          />
          <Input
            label="To (inclusive)"
            name="export-to"
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
          />
          <Button variant="primary" onClick={() => void run()} loading={busy} disabled={invalid}>
            Download CSV
          </Button>
        </Flex>
        <Text role="small" color="secondary" as="p">
          {current.description}
        </Text>
        {invalid ? (
          <Text role="small" as="p" style={{ color: "var(--color-danger)" }}>
            Choose a start date that is not after the end date.
          </Text>
        ) : null}
        {error ? (
          <Alert tone="danger" title="Export failed">
            {error}
          </Alert>
        ) : null}
        {last?.truncated ? (
          <Alert tone="warning" title="The export was cut off">
            Only the first {formatCount(last.rows)} rows were included. Choose a shorter period and export again so nothing is missing.
          </Alert>
        ) : null}
        {last && last.rows === 0 ? (
          <Text role="small" color="secondary" as="p">
            No {current.label.toLowerCase()} in that period, so no file was produced.
          </Text>
        ) : null}
      </Panel>
      <Text role="caption" color="muted" as="p">
        Exporting needs the Finance manage permission because the file carries every amount and provider reference. Cells that begin with = + - or @ are prefixed so a spreadsheet never runs them as formulas.
      </Text>
    </Stack>
  );
}
